import { create } from 'zustand'
import { ACCESS_TOKEN, getCookie, setCookie, removeCookie } from '@/lib/cookies'

// Dynamic types - roles and permissions come from backend
export type Role = string // e.g., 'ADMIN', 'SUPER_ADMIN', 'MODERATOR', etc.
export type Permission = string // e.g., 'users.view', 'users.create', 'posts.delete'

export interface AuthUser {
  id: string
  accountNo?: string
  email: string
  phone?: string | null
  username: string
  avatarUrl?: string | null
  coverUrl?: string | null
  type: 'STAFF' | 'USER' | 'REGULAR'
  isSuperAdmin: boolean
  roles: Role[]
  permissions: Permission[]
  exp: number
}

interface AuthState {
  auth: {
    user: AuthUser | null
    accessToken: string
    setUser: (user: AuthUser | null) => void
    setAccessToken: (accessToken: string) => void
    resetAccessToken: () => void
    reset: () => void
    isAuthenticated: () => boolean
    login: (email: string, password: string) => Promise<boolean>
    logout: () => void
    refreshToken: () => Promise<boolean>

    // RBAC methods
    hasRole: (role: Role | Role[]) => boolean
    hasPermission: (permission: Permission | Permission[]) => boolean
    isSuperAdmin: () => boolean
    canAccess: (
      requiredPermissions?: Permission[],
      requiredRoles?: Role[]
    ) => boolean
  }
}

// Initialize from cookies
const cookieState = getCookie(ACCESS_TOKEN)
const initToken =
  cookieState && cookieState !== 'undefined'
    ? cookieState // Token is already a string, no need to parse
    : ''

// Try to restore user from sessionStorage
const initUser =
  typeof window !== 'undefined'
    ? (() => {
        try {
          const stored = sessionStorage.getItem('auth_user')
          return stored ? JSON.parse(stored) : null
        } catch {
          return null
        }
      })()
    : null

export const useAuthStore = create<AuthState>()((set, get) => ({
  auth: {
    user: initUser,
    accessToken: initToken,

    setUser: (user) =>
      set((state) => {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log('[Auth Debug] setUser called:', user)
        }

        // Save user to sessionStorage for persistence
        if (typeof window !== 'undefined' && user) {
          sessionStorage.setItem('auth_user', JSON.stringify(user))
        } else if (typeof window !== 'undefined' && !user) {
          sessionStorage.removeItem('auth_user')
        }
        return {
          ...state,
          auth: { ...state.auth, user },
        }
      }),

    setAccessToken: (accessToken) =>
      set((state) => {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log('[Auth Debug] setAccessToken called:', {
            tokenLength: accessToken?.length,
            tokenPreview: accessToken?.substring(0, 10) + '...',
          })
        }

        // Store token directly without double JSON.stringify
        setCookie(ACCESS_TOKEN, accessToken, {
          maxAge: 60 * 60 * 24 * 7, // 7 days
          secure: import.meta.env.PROD,
          sameSite: 'strict',
        })
        return {
          ...state,
          auth: { ...state.auth, accessToken },
        }
      }),

    resetAccessToken: () =>
      set((state) => {
        removeCookie(ACCESS_TOKEN)
        return {
          ...state,
          auth: { ...state.auth, accessToken: '' },
        }
      }),

    reset: () =>
      set((state) => {
        removeCookie(ACCESS_TOKEN)
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('auth_user')
        }
        return {
          ...state,
          auth: { ...state.auth, user: null, accessToken: '' },
        }
      }),

    isAuthenticated: (): boolean => {
      const state = get()
      const isValid = !!state.auth.accessToken && !!state.auth.user

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[Auth Debug] isAuthenticated check:', {
          hasToken: !!state.auth.accessToken,
          hasUser: !!state.auth.user,
          isValid,
        })
      }

      // Check token expiration
      if (isValid && state.auth.user?.exp) {
        const isExpired = Date.now() > state.auth.user.exp
        if (isExpired) {
          state.auth.reset()
          return false
        }
      }

      return isValid
    },

    // RBAC Methods
    hasRole: (role: Role | Role[]): boolean => {
      const state = get()
      if (!state.auth.user) return false

      // Super admins have all roles
      if (state.auth.user.isSuperAdmin) return true

      const roles = Array.isArray(role) ? role : [role]
      return roles.some((r) => state.auth.user?.roles.includes(r))
    },

    hasPermission: (permission: Permission | Permission[]): boolean => {
      const state = get()
      if (!state.auth.user) return false

      // Super admins have all permissions
      if (state.auth.user.isSuperAdmin) return true

      const permissions = Array.isArray(permission) ? permission : [permission]
      return permissions.some((p) => state.auth.user?.permissions.includes(p))
    },

    isSuperAdmin: (): boolean => {
      const state = get()
      return state.auth.user?.isSuperAdmin || false
    },

    canAccess: (
      requiredPermissions?: Permission[],
      requiredRoles?: Role[]
    ): boolean => {
      const state = get()

      if (!state.auth.user) return false

      // Super admins can access everything
      if (state.auth.isSuperAdmin()) return true

      // Check permissions (OR logic - user needs at least one)
      if (requiredPermissions && requiredPermissions.length > 0) {
        if (state.auth.hasPermission(requiredPermissions)) return true
      }

      // Check roles (OR logic - user needs at least one)
      if (requiredRoles && requiredRoles.length > 0) {
        if (state.auth.hasRole(requiredRoles)) return true
      }

      // If no requirements specified, allow access
      if (!requiredPermissions?.length && !requiredRoles?.length) return true

      return false
    },

    login: async (_email: string, _password: string): Promise<boolean> => {
      // This method is deprecated - use useLogin hook from @/lib/api/hooks instead
      // Keeping for backward compatibility
      // eslint-disable-next-line no-console
      console.warn('auth.login() is deprecated. Use useLogin() hook instead.')
      return false
    },

    logout: () => {
      set((state) => {
        removeCookie(ACCESS_TOKEN)

        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('auth_user')
        }

        return {
          ...state,
          auth: {
            ...state.auth,
            user: null,
            accessToken: '',
          },
        }
      })
    },

    refreshToken: async (): Promise<boolean> => {
      // This method is deprecated - token refresh should be handled by API interceptors
      // Keeping for backward compatibility
      // eslint-disable-next-line no-console
      console.warn(
        'auth.refreshToken() is deprecated. Handle refresh via API interceptors.'
      )
      return false
    },
  },
}))
